import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';

import { map } from 'rxjs/operators';
import { User } from '@mitel/cloudlink-sdk';
import { AdminService } from '../services/admin.service';
import { ActionType, BaseRoleType, Permission, rbacPolicyConst, rbacUtil, ManagementProfile } from '../utils/rbac.util';
import { UserRoles } from '../constants/constants';
import { MenuItem } from '../interfaces/menu-item';
import xLaunchAdmin from '../../../assets/rbac-permissions/x-launch-admin.json'
import supportPortalAdmin from '../../../assets/rbac-permissions/support-portal-admin.json'
import userSource from '../../../assets/rbac-permissions/user-source.json'

@Injectable({
  providedIn: 'root'
})

export class RBACAuthGuardService  {
  permissions: Permission[] = [];
  permissions_temp: Permission[] = [];
  public validPermissionsFlag: Boolean = false;
  public validPermissionsHandled = new Subject<Boolean>(); //used for refresh
  DO_NOT_ROUTE = '/norouting';
  _managementProfile: ManagementProfile;

  get managementProfile() {
    return this._managementProfile;
  }

  constructor(private adminService: AdminService) {}

  setPermissions(resourcePermissions: string[]) {
    this.permissions = [];
    if(resourcePermissions){
      resourcePermissions.forEach(permission => {
        try {
          let paths = permission.replace(rbacPolicyConst.actionFeature, '').split('/');
          if(paths.length > 2){// role/category/action
            this.permissions.push({ role: paths[0], category: paths[1], action: paths[2], resource: permission });
          }
        }
        catch{
          console.error(`Error while reading the permission: ${permission}`);
        }
      });
    }
	}

  setValidPermissionsFlag(){
    this.validPermissionsFlag = true;
    this.validPermissionsHandled.next(this.validPermissionsFlag);
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    if(this.validPermissionsFlag){
      let denied = this.isPermissionDeniedForUser(route.data['category'], route.data['action']);
      if(denied){ 
        console.warn('Trying to access a restricted route - rbac service');
      }
      return of(!denied);
    }else {
      return this.validPermissionsHandled.asObservable().pipe(map(permissions => {
        let denied = false;
        if(permissions){
          denied = this.isPermissionDeniedForUser(route.data['category'], route.data['action']);
          if(denied){
            console.warn('Trying to access a restricted route - rbac service');
          }
        }
        return !denied;
      }));
    }
  }
    
  isPermissionDeniedForUser(category: string, action: string): boolean {
    let isDenied = false;
    if(this.permissions && this.permissions.length > 0){
      isDenied = Boolean(this.permissions.find(permission => {
          return rbacUtil.compareStringsCaseInsensitive(category, permission.category) && 
                 rbacUtil.compareStringsCaseInsensitive(action, permission.action)}));
    }
    return isDenied;
  }

  async setRBACPermissionsByUser(user: User) {
    //rbac roles only applied to partners and admins
    if(user.role === UserRoles.PARTNER_ADMIN || user.role === UserRoles.ACCOUNT_ADMIN){
      let rbacUserTag = rbacUtil.getTagPropertyById(user, rbacPolicyConst.rbacUserTagName, rbacPolicyConst.rbacUserTagId);
      if(rbacUserTag){
        await this.adminService.getPolicy(user.accountId, rbacUserTag).then(policy =>{
          let allowedRoutes: string = '';
          if (policy.statements && policy.statements.length > 0) {
            let baseRoleTypes = Object.keys(BaseRoleType).map(brt => BaseRoleType[brt]);
            policy.statements.forEach(s => {
              //statementId = BaseRoleType
              if (s.statementId && baseRoleTypes.includes(s.statementId.toLocaleUpperCase())) {
                this.setPermissions(s.resource);
              }
              if (s.statementId === rbacPolicyConst.allowedRoutes) {
                allowedRoutes = s.condition;
              }
            });
          }

          // Management Profile extends a standard RBAC (Role-Based Access Control) role definition
          //  with additional properties such as allowed locations and assigned administrators
          if (policy?.policyId?.includes(rbacPolicyConst.rbacManagementProfilePolicyId) && allowedRoutes) {
            const indexSplitName = policy.name.indexOf(rbacPolicyConst.divider);
            this._managementProfile = {
              id: policy?.policyId,
              name: indexSplitName > 0 ? policy.name.substring(0, indexSplitName) : policy.name,
              description: indexSplitName > 0 ? policy.name.substring(indexSplitName + 1, policy.name.length) : '',
              locations: allowedRoutes.split('||')?.map(condition => condition.split('/')?.pop())
            } as ManagementProfile;
          }
        })
        .catch(err => {
          console.log(`Error occurred while reading the policy for account ${user.accountId}, policyId ${rbacUserTag}, error ${err}`);
        });
      }
      this.setValidPermissionsFlag();
    }
  }

  setCrossLaunchPermissionsByUser() {
    this.readPermissionsFromJsonFile(xLaunchAdmin);
  }

  setSupportPortalPermissionsByUser() {
    this.readPermissionsFromJsonFile(supportPortalAdmin);
  }

  setUserSourcePermissions() {
    //save current permissions as this change is temporary
    this.permissions_temp = JSON.parse(JSON.stringify(this.permissions)); // save current permission as this change is temporary
    this.readPermissionsFromJsonFile(userSource);
  }

  restorePermissions(){
    this.permissions = JSON.parse(JSON.stringify(this.permissions_temp));
    this.setValidPermissionsFlag();
  }

  readPermissionsFromJsonFile(file: any ): void {
    let resources = [];
    if(file.categories){
      file.categories.forEach(c => {
        c.actions.forEach(a => {
          if(!a.allow){ //permission restricted
            resources.push(a.resource);
          }
        })
      });
      this.setPermissions(resources);
      this.setValidPermissionsFlag();
    }else{
      console.warn(`File permissions are not defined for ${file}`);
    }
  }

  filterMenuByPermissions(menuItems: MenuItem[]): MenuItem[] {
    if(this.permissions && this.permissions.length > 0){
      menuItems = menuItems.filter(item => !this.permissions.find(permission => {
        return rbacUtil.compareStringsCaseInsensitive(item.id, permission.category) && rbacUtil.compareStringsCaseInsensitive(permission.action, ActionType.VIEW);
      }));

      menuItems.forEach(item => {
        if(item.children){
          item.children = item.children.filter(item => !this.permissions.find(permission => {
            return rbacUtil.compareStringsCaseInsensitive(item.id, permission.category) && rbacUtil.compareStringsCaseInsensitive(permission.action, ActionType.VIEW);
          }));
        }
      });

      menuItems = menuItems.filter(item => !(item.navigation === this.DO_NOT_ROUTE && item.children.length === 0));
    }
    return menuItems;
  }
}
